import { useId, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '@/stores/useAuthStore';
import { GoogleSignInButton } from '@/components/auth/GoogleSignInButton';
import { isGerman } from '@/i18n/locale';

export default function Login() {
  const signIn = useAuthStore((s) => s.signIn);
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const de = isGerman();
  // AuthCallback bounces here with ?error=oauth_failed when Google returned
  // without a session (user cancelled, or the provider denied). Without this
  // the mum lands on a silent login form and doesn't know what happened.
  const [params] = useSearchParams();
  const oauthFailed = params.get('error') === 'oauth_failed';
  const [error, setError] = useState<string | null>(
    oauthFailed
      ? de
        ? 'Die Anmeldung mit Google wurde abgebrochen. Bitte versuch es nochmal oder melde dich per E-Mail an.'
        : 'Google sign-in didn’t complete. Please try again, or sign in with email.'
      : null
  );
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const res = await signIn(email, password);
    setLoading(false);
    if (res.error) {
      setError(res.error);
      return;
    }
    navigate('/today', { replace: true });
  };

  return (
    <AuthShell
      title={de ? 'Willkommen zurück' : 'Welcome back'}
      subtitle={de ? 'Melde dich bei deinem Alma-Konto an' : 'Sign in to your Alma account'}
    >
      <GoogleSignInButton onError={(msg) => setError(msg)} />

      <div className="relative my-5 flex items-center">
        <div className="flex-1 h-px bg-primary-100" />
        <span className="px-3 text-[11px] uppercase tracking-wider text-slate-muted">
          {de ? 'oder' : 'or'}
        </span>
        <div className="flex-1 h-px bg-primary-100" />
      </div>

      <form onSubmit={onSubmit} className="space-y-4">
        <Field
          label={de ? 'E-Mail' : 'Email'}
          type="email"
          autoComplete="email"
          value={email}
          onChange={setEmail}
          required
        />
        <Field
          label={de ? 'Passwort' : 'Password'}
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={setPassword}
          required
        />
        {error && (
          <div className="rounded-lg bg-danger/5 border border-danger/30 px-3 py-2 text-sm text-danger-700" role="alert">
            {error}
          </div>
        )}
        <button type="submit" disabled={loading} className="btn-primary w-full">
          {loading
            ? (de ? 'Anmeldung läuft…' : 'Signing in…')
            : (de ? 'Anmelden' : 'Sign in')}
        </button>
      </form>
      <p className="text-sm text-slate-muted text-center mt-6">
        {de ? 'Neu bei Alma?' : 'New to Alma?'}{' '}
        <Link to="/signup" className="text-primary-700 hover:text-primary-800 font-medium">
          {de ? 'Konto erstellen' : 'Create an account'}
        </Link>
      </p>
    </AuthShell>
  );
}

// Shared auth shell + field — also used by Signup
export function AuthShell({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) {
  return (
    <div className="min-h-[100dvh] bg-cream flex flex-col">
      <header className="px-6 py-5">
        <Link to="/" className="inline-flex items-center gap-2.5 rounded-lg" aria-label="Alma">
          <div className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center shadow-sm" aria-hidden>
            <span className="text-white font-display font-semibold">A</span>
          </div>
          <span className="font-display text-xl font-semibold text-slate">Alma</span>
        </Link>
      </header>
      <main className="flex-1 flex items-center justify-center px-6 pb-12">
        <div className="w-full max-w-sm">
          <h1 className="font-display text-3xl font-semibold text-slate text-center">{title}</h1>
          <p className="text-slate-muted text-center mt-2 mb-8">{subtitle}</p>
          {children}
        </div>
      </main>
    </div>
  );
}

export function Field({
  label,
  value,
  onChange,
  hint,
  ...rest
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  hint?: string;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'>) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  return (
    <div>
      <label htmlFor={id} className="text-sm text-slate font-medium mb-1.5 block">
        {label}
      </label>
      <input
        {...rest}
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-describedby={hintId}
        className="input-field"
      />
      {hint && (
        <p id={hintId} className="mt-1.5 text-[12px] text-slate-muted">
          {hint}
        </p>
      )}
    </div>
  );
}
